import { IApiBaseProps } from '@common/api.models';
import {
  StringFilter,
  OptionFilter,
  DateTimeFilter,
  BooleanFilter,
  OptionsFilter,
} from '@common/searchFilter/searchFilter.models';
import { IProjectCompanyBase, IProjectCompanyOption } from '@company/projectCompany/projectCompany.models';
import { IRiskCategory } from '@riskCategory/riskCategory.models';
import { IProjectUserProfile } from '@userProfile/projectUserProfile.models';
import { ITag } from 'src/tag/tag.models';
import { IDownloadFileBaseProps } from '@file/file.models';
import { IProjectTeam } from '@projectTeam/projectTeam.models';
import { IResponsibleUser } from './detail/masterScheduleDetail.models';

export interface IMasterScheduleHeader {
  id: 0;
  refNo: string;
  isHeader: boolean;
  headerId: number;
}

export interface IMasterScheduleBase {
  id: number;
  refNo: string;
  designPackage?: string;
  riskCategory?: IRiskCategory;
}

export interface IProjectMasterScheduleBase extends Omit<IMasterScheduleBase, 'riskCategory'> {
  riskCategory?: string;
  voItem?: string;
  nominatedDesignerProjectCompanyId?: number;
  nominatedCheckerProjectCompanyId?: number;
  nominatedReviewerProjectCompanyId?: number;
  designBrief?: string;
  relatedDesignChange?: string;
  relatedConstructionAndRemovalCert?: string;
  temporaryWorksRiskCategory?: string;
  relevantContractWidePackage?: string;
  responsibleSiteEngineer?: string;
  worksProcedureSupplementRequired?: boolean;
  targetDateForWorksProcedure?: string;
  remarks?: string;
  isDeleted: boolean;
  isCancelled?: boolean;
  isLocked?: boolean;
  startConstructionDate?: string;
  removalRedundantDate?: string;
  procurementFabricationDate?: string;
  rowVersion?: string;
}

export interface IMasterSchedule {
  id: number;
  refNo: string;
  designPackage?: string;
  riskCategory?: IRiskCategory;
  isCancelled: boolean;
  isLocked?: boolean;

  responsibleDesigner?: IProjectCompanyOption;
  responsibleChecker?: IProjectCompanyOption;
  responsibleReviewer?: IProjectCompanyOption;
  responsibleTwc?: IResponsibleUser[];
  responsibleTwcm?: IResponsibleUser[];
  responsibleSrp?: IResponsibleUser[];
  startConstructionDate?: string;

  tags?: ITag[];
  isHeader?: boolean;
  headerId?: number;
}

export interface IMasterScheduleSearchFilter {
  refNo?: StringFilter;
  designPackage?: StringFilter;
  riskCategory?: OptionFilter;

  designer?: OptionFilter;
  checker?: OptionFilter;
  twc?: OptionsFilter;
  srp?: OptionsFilter;
  voItem?: OptionFilter;

  isCancelled?: BooleanFilter;
  isLocked?: BooleanFilter;

  startConstructionDate?: DateTimeFilter;
  procurementFabricationDate?: DateTimeFilter;
}

export interface IMasterScheduleOption extends IMasterScheduleBase {
  displayName: string;
  isCancelled: boolean;
  isLocked: boolean;
}

export interface IMasterScheduleUserOption {
  id: number;
  projectId: number;
  projectMasterScheduleId: number;
  projectUserProfileId: number;
  projectUserProfile: IProjectUserProfile;
  removed?: boolean;
  rowVersion: string;
}

export interface IMasterScheduleSearchResult {
  id: number;
  projectId: number;
  refNo: string;
  designPackage?: string;
  riskCategory?: string;
  voItem?: string;
  nominatedDesignerProjectCompanyId?: number;
  nominatedDesignerProjectCompany?: IProjectCompanyBase;
  nominatedCheckerProjectCompanyId?: number;
  nominatedCheckerProjectCompany?: IProjectCompanyBase;

  srp?: IMasterScheduleUserOption[];
  twc?: IMasterScheduleUserOption[];
  twcm?: IMasterScheduleUserOption[];

  isCancelled?: boolean;
  isLocked?: boolean;
  startConstructionDate?: string;
  procurementFabricationDate?: string;
  headerName?: string;
  isHeader?: boolean;
}

export interface IProjectMasterScheduleOption {
  id: number;
  refNo?: string;
  designPackage?: string;
  riskCategory: string;
  displayName?: string;
  isCancelled: boolean;
  isLocked: boolean;
}

export interface IProjectMasterScheduleOptionWithRoles {
  id: number;
  refNo: string;
  designPackage?: string;
  isNominatedDesigner: boolean;
  isNominatedChecker: boolean;
  isNominatedReviewer: boolean;
  isNominatedSrp: boolean;
  isNominatedTwc: boolean;
  isNominatedTwcm: boolean;
}

export interface IProjectMasterScheduleTwcOption {
  id: number;
  projectId: number;
  projectMasterScheduleId: number;
  projectUserProfileId: number;
  removed: boolean;
  rowVersion?: string;
  uid?: string;
  upn?: string;
  email?: string;
  displayName?: string;
  projectUserProfile: IProjectUserProfile;
}

export interface IMasterScheduleTwcOptionsForLoadingRemovalCertRequest extends IApiBaseProps {
  masterScheduleId?: number;
}
export enum EMasterScheduleSearchKey {
  REF_NO = 'refNo',
  DESIGN_PACKAGE = 'designPackage',
  RISK_CATEGORY = 'riskCategory',
  VO_ITEM = 'voItem',
  DESIGNER = 'designer',
  CHECKER = 'checker',
  TWC = 'twc',
  TWCM = 'twcm',
  SRP = 'srp',
  IS_CANCELLED = 'isCancelled',
  IS_LOCKED = 'isLocked',
  START_CONSTRUCTION_DATE = 'startConstructionDate',
  PROCUREMENT_FABRICATION_DATE = 'procurementFabricationDate',
}

export interface IUpsertProjectTeamMasterScheduleRequest extends IProjectTeamMasterScheduleBase {}

export interface IProjectTeamMasterScheduleBase {
  teamName: string;
  id: number;
  projectMasterScheduleId: number;
  projectTeamId: number;
  projectId: number;
  rowVersion: string;
}
export interface IProjectTeamMasterScheduleAssignProjectMasterScheduleOption {
  projectMasterSchedules: IProjectMasterScheduleOptionWithProjectTeamMasterSchedule[];
  projectTeams: IProjectTeam[];
}
export interface IProjectMasterScheduleOptionWithProjectTeamMasterSchedule extends IProjectMasterScheduleBase {
  projectTeamMasterSchedules: IProjectTeamMasterScheduleBase[];
}

export interface IProjectMasterScheduleOptionWithProjectTeamMasterScheduleRequest {
  projectId: number;
  projectCompanyId: number;
}

export interface IProjectMasterScheduleQrPdfWithIdRequest extends IDownloadFileBaseProps {
  id: number;
}
